import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import { NetViewWebService } from './netview.web.service';
import { NetViewTreeDto, NetViewDto, NetViewTreeNodeDto } from 'UvExplorerServer.Dtos.TS/NetViews';
import { AgentDto } from "UvExplorerServer.Dtos.TS/Agents";
import { UserDto } from "UvExplorerServer.Dtos.TS/Auth";
import { ScheduledDiscovery, DiscoverySettings, DiscoveryMethod } from 'DiscoveryEngine.TS/Discovery';
import { Guid } from 'DiscoveryEngine.TS/Utils';
import { IdentityService } from 'identity';

@Injectable()
export class NetViewService implements OnDestroy {

	public netviews: NetViewDto[] = [];
	public roots: NetViewDto[] = [];
	public networks: NetViewDto[] = [];
	public agents: AgentDto[] = [];

	public netViewTreeLoaded: Subject<any>;
	public onNetViewTreeLoaded: Observable<any>;

	public netViewTreeChanged: Subject<any>;
	public onNetViewTreeChanged: Observable<any>;

	public netViewTreeUnloaded: Subject<any>;
	public onNetViewTreeUnloaded: Observable<any>;

	private loginSub: Subscription;
	private logoutSub: Subscription;

	constructor(private identityService: IdentityService, private netViewWebService: NetViewWebService) {
		// NetView tree was loaded from the server
		this.netViewTreeLoaded = new Subject<any>();
		this.onNetViewTreeLoaded = this.netViewTreeLoaded.asObservable();

		// Updates/changes/modifications were made to the NetView tree
		this.netViewTreeChanged = new Subject<any>();
		this.onNetViewTreeChanged = this.netViewTreeChanged.asObservable();

		// NetView tree was unloaded at logout
		this.netViewTreeUnloaded = new Subject<any>();
		this.onNetViewTreeUnloaded = this.netViewTreeUnloaded.asObservable();

		if (identityService.isLoggedIn) {
			this.populateNetViewTree();
		}

		this.loginSub = identityService.onLogin.subscribe((user: UserDto) => {
			this.populateNetViewTree();
		});

		this.logoutSub = identityService.onLogout.subscribe(() => {
			this.netviews = [];
			this.roots = [];
			this.networks = [];
			this.agents = [];

			this.netViewTreeUnloaded.next(true);
		});
	}

	public ngOnDestroy(): void {
		if (this.loginSub) {
			this.loginSub.unsubscribe();
			this.loginSub = undefined;
		}
		if (this.logoutSub) {
			this.logoutSub.unsubscribe();
			this.logoutSub = undefined;
		}
	}

	public get isNetViewTreeLoaded(): boolean {
		return (this.netviews.length > 0);
	}

	public getNetViewTree(dataSelector: string): Observable<NetViewTreeDto> {
		return this.netViewWebService.getNetViewTree(dataSelector);
	}

	public reloadNetViewTree(): Observable<NetViewTreeDto> {
		return this.populateNetViewTree();
	}

	private populateNetViewTree(): Observable<NetViewTreeDto> {
		let subject = new Subject<NetViewTreeDto>();

		this.netViewWebService.getNetViewTree("all").subscribe(
			(result: NetViewTreeDto) => {

				this.agents = result.Agents.ToArray();
				this.netviews = result.NetViews.ToArray(); // Gets all netviews INCLUDING ROOTS
				this.networks = [];
				this.roots = [];

				this.netviews.forEach((netview: NetViewDto) => {
					if (netview.ScopeGuid == "") {
						this.roots.push(netview);
					}
					else {
						this.networks.push(netview);
					}
				});

				subject.next(result);
				this.netViewTreeLoaded.next(true);
			},
			(error?: any) => {
				console.log(JSON.stringify(error));
				subject.error(error);
			});

		return subject.asObservable();
	}

	public getNetViewFromTree(guid: string): NetViewDto {
		for (var netview in this.netviews) {
			var view: NetViewDto = this.netviews[netview];
			if (view.Guid == guid) {
				return view;
			}
		}
		return null;
	}

	public getAgentFromTree(guid: string): AgentDto {
		for (var agent in this.agents) {
			var view: AgentDto = this.agents[agent];
			if (view.Guid == guid) {
				return view;
			}
		}
		return null;
	}

	public getNetViewFromServer(guid: string): Observable<NetViewDto> {
		return this.netViewWebService.getNetViewByGuid(guid);
	}

	public getAgentFromServer(agentGuid: string): Observable<AgentDto> {
		return this.netViewWebService.getAgentByGuid(agentGuid);
	}


	public unbindAgent(agent: AgentDto): Observable<boolean> {
		return this.netViewWebService.unbindAgent(agent);
	}

	public getNetViewParent(childGuid: string): NetViewDto {
		for (let i = 0; i < this.netviews.length; ++i) {
			let netview = this.netviews[i];
			if (netview.NetViewGuids.Contains(childGuid)) {
				return netview;
			}
		}
		return null;
	}

	public getAgentParent(childGuid: string): NetViewDto {
		for (let i = 0; i < this.netviews.length; ++i) {
			let netview = this.netviews[i];
			if (netview.AgentGuids.Contains(childGuid)) {
				return netview;
			}
		}
		return null;
	}

	public getSchedDiscParent(schedDiscGuid: string): AgentDto {
		for (let i = 0; i < this.agents.length; ++i) {
			let agent = this.agents[i];
			for (let j = 0; j < agent.ScheduledDiscoveries.Count; ++j) {
				let schedDisc = agent.ScheduledDiscoveries.Get(j);
				if (schedDisc.GUID === schedDiscGuid) {
					return agent;
				}
			}
		}
		return null;
	}

	public getSchedDiscSettings(schedDiscGuid: string, discSettingsGuid: string): DiscoverySettings {
		let settings: DiscoverySettings = null;

		let agent: AgentDto = this.getSchedDiscParent(schedDiscGuid);
		if (agent) {
			agent.DiscoverySettings.Some((ds: DiscoverySettings) => {
				if (ds.GUID == discSettingsGuid) {
					settings = ds;
					return true;
				}
				else {
					return false;
				}
			}, this);
		}

		return settings;
	}

	public get rootForCurrentUser(): NetViewDto {
		let result: NetViewDto = null;

		this.roots.some((nv: NetViewDto) => {
			if (nv.UserGuid === this.identityService.user.Guid) {
				result = nv;
				return true;
			}
			else {
				return false;
			}
		});

		return result;
	}

	/*PRIVATE*/ public addNetViewToTree(newNetView: NetViewDto): void {
		this.rootForCurrentUser.NetViewGuids.Add(newNetView.Guid);
		this.netviews.push(newNetView);
		this.networks.push(newNetView);
	}

	/*PRIVATE*/ public removeNetViewFromTree(guid: string): void {
		let parent: NetViewDto = this.getNetViewParent(guid);
		if (parent) {
			parent.NetViewGuids.Remove(guid);

			for (let i = 0; i < this.netviews.length; ++i) {
				if (this.netviews[i].Guid == guid) {
					this.netviews.splice(i, 1);
					break;
				}
			}
			for (let i = 0; i < this.networks.length; ++i) {
				if (this.networks[i].Guid == guid) {
					this.networks.splice(i, 1);
					break;
				}
			}
		}
	}

	/*PRIVATE*/ public addAgentToTree(newAgent: AgentDto, parent: NetViewDto): void {
		parent.AgentGuids.Add(newAgent.Guid);
		this.agents.push(newAgent);
	}

	/*PRIVATE*/ public removeAgentFromTree(guid: string): void {
		let parent: NetViewDto = this.getAgentParent(guid);
		if (parent) {
			parent.AgentGuids.Remove(guid);

			for (let i = 0; i < this.agents.length; ++i) {
				if (this.agents[i].Guid == guid) {
					this.agents.splice(i, 1);
					break;
				}
			}
		}
	}

	public getProtocolSettingsReferences(psGuid: string): string[] {
		psGuid = psGuid.toLowerCase();

		let nodes: NetViewTreeNodeDto[] = [...this.netviews, ...this.agents];

		let result: string[] = [];

		nodes.forEach((node: NetViewTreeNodeDto) => {
			node.DiscoverySettings.ForEach((ds: DiscoverySettings) => {
				if (ds.ProtocolSettings.Some((guid: Guid) => {
					return (guid.ToString().toLowerCase() == psGuid);
				}, this)) {
					result.push(ds.Name);
				}
			}, this);
			node.ScheduledDiscoveries.ForEach((sd: ScheduledDiscovery) => {
				if (sd.PrtgExport.HasServerSettings) {
					if (sd.PrtgExport.ServerSettings.ToString().toLowerCase() == psGuid) {
						result.push(sd.Name);
					}
				}
			}, this);
		}, this);

		return result;
	}

}